import { z } from 'zod';
import { rcrmCandidateSchema, rcrmCompanySchema, rcrmContactSchema, rcrmJobSchema, rcrmNoteSchema, } from './entities.js';
/**
 * RecruitCRM webhooks: subscriptions and what a delivery carries.
 * Evidence: Relay nitpicks 2026-06-25 (no event label in the body), 2026-08-03 (the
 * `/subscriptions` endpoint and its inventory), 2026-07-03 (rotating the token deletes them).
 */
/**
 * Event names seen on the account's subscriptions (Relay nitpicks 2026-07-03, 2026-08-03,
 * RELAY-1045). `company.*` is from the official docs: no company subscription or payload was
 * ever recorded. RCRM offers no `note.deleted`.
 */
export const RCRM_WEBHOOK_EVENTS = [
    'candidate.created',
    'candidate.updated',
    'candidate.deleted',
    'candidate.assigned',
    'candidate.unassigned',
    'candidate.hiringstage.updated',
    'candidate.external.applied.jobs',
    'contact.created',
    'contact.updated',
    'company.created',
    'company.updated',
    'job.created',
    'job.updated',
    'job.status.updated',
    'deal.created',
    'deal.updated',
    'note.created',
    'note.updated',
];
/**
 * `POST /v1/subscriptions` body. Several subscriptions per event are allowed; there is no
 * update (PUT/PATCH → 405), so a repoint is create-then-delete. `target_url` is called exactly
 * as registered, query string included — a receiver's secret rides there, and the list endpoint
 * shows it in plain text.
 */
export const rcrmSubscriptionCreateSchema = z.object({
    event: z.string().min(1),
    target_url: z.string().min(1),
});
export const rcrmSubscriptionSchema = z.looseObject({
    id: z.number(),
    event: z.string(),
    target_url: z.string(),
});
/** `GET /v1/subscriptions?page=n` — `{ data, next_page_url }` (the rest of the envelope unrecorded). */
export const rcrmSubscriptionListSchema = z.looseObject({
    data: z.array(rcrmSubscriptionSchema),
    next_page_url: z.string().nullish(),
});
/**
 * A delivery body is the FLAT entity object: no event name, no `event_type`, no `data` wrapper
 * (observed for notes, jobs and contacts; candidates assumed the same). An update also carries
 * `fields_changed` — its shape was never recorded (`apps/rcrm-mock` sends an array of field
 * names), so it is `unknown` here; its PRESENCE is what tells an update from a create.
 * Fields are all optional: RCRM sends partial payloads, and a receiver must not treat an absent
 * field as a cleared one.
 */
const fieldsChanged = { fields_changed: z.unknown().optional() };
export const rcrmCandidateWebhookSchema = rcrmCandidateSchema.partial().extend(fieldsChanged);
export const rcrmContactWebhookSchema = rcrmContactSchema.partial().extend(fieldsChanged);
/** UNCONFIRMED: no company payload was ever recorded; this is the REST shape. */
export const rcrmCompanyWebhookSchema = rcrmCompanySchema.partial().extend(fieldsChanged);
export const rcrmJobWebhookSchema = rcrmJobSchema.partial().extend(fieldsChanged);
export const rcrmNoteWebhookSchema = rcrmNoteSchema.partial().extend(fieldsChanged);
/** Created or updated, the only way a payload says it: `fields_changed` is present on updates. */
export function rcrmWebhookAction(payload) {
    return 'fields_changed' in payload ? 'updated' : 'created';
}
// ── The CRM's receiver (TS-1858) ────────────────────────────────────────────────────────────
/**
 * The CRM tables an RCRM webhook can update, one target URL each
 * (`/integrations/recruitcrm/webhooks/<entity>`): RCRM's body never says which entity it is about,
 * so the path does. Only the values of linked custom fields (`field_identities`) are received —
 * and a deal's stage, which moves its project in the pipeline linked `both` to RCRM Deals
 * (`pipeline_identities`), and a candidate's Stage.
 */
export const RCRM_RECEIVER_ENTITIES = [
    'candidates',
    'companies',
    'contacts',
    'jobs',
    'deals',
];
export const rcrmReceiverEntitySchema = z.enum(RCRM_RECEIVER_ENTITIES);
/** The RCRM word for each receiver entity: the nested key and the `resource_url` segment. */
export const RCRM_RECEIVER_SINGULAR = {
    candidates: 'candidate',
    companies: 'company',
    contacts: 'contact',
    jobs: 'job',
    deals: 'deal',
};
/**
 * The stage label a delivered (or read) RCRM deal names, or null when it names none the CRM can
 * match by: production reads `deal_stage` as `{ id, label, percentage }`
 * (`docs/nitpicks/recruitcrm/deal-stage-write.md`) and the docs say a string. A bare id (a number,
 * or a string of digits) is NOT a label — the record is read back instead. Never a value logged.
 */
export function rcrmDealStageLabel(record) {
    const stage = record['deal_stage'];
    const label = asRecord(stage)?.['label'] ?? stage;
    if (typeof label !== 'string')
        return null;
    const trimmed = label.trim();
    return trimmed === '' || /^\d+$/.test(trimmed) ? null : trimmed;
}
const DEAL_JOB_SLUG_KEYS = ['job_slug', 'additional_job_slugs', 'job_slugs'];
/**
 * The jobs an RCRM deal names (`job_slug`, `additional_job_slugs`, `job_slugs`), as slugs (at most
 * 200). `null` when the record carries none of those keys at all — a thin delivery says nothing
 * about the deal's jobs, so the CRM touches nothing — while a key present with no slug is `[]`.
 */
export function rcrmDealJobSlugs(record) {
    if (!DEAL_JOB_SLUG_KEYS.some((k) => k in record))
        return null;
    const slugs = new Set();
    for (const key of DEAL_JOB_SLUG_KEYS) {
        const v = record[key];
        const raw = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [];
        for (const s of raw) {
            const text = typeof s === 'string' ? s.trim() : typeof s === 'number' ? String(s) : '';
            if (text !== '')
                slugs.add(text);
        }
    }
    return [...slugs].slice(0, 200);
}
function asRecord(v) {
    return v !== null && typeof v === 'object' && !Array.isArray(v)
        ? v
        : null;
}
function namesARecord(v) {
    return v !== null && ['id', 'slug', 'resource_url', 'url'].some((k) => v[k] != null);
}
/**
 * The RCRM record a delivery is about. The flat body is the common case
 * (`docs/nitpicks/recruitcrm/webhook-flat-payload.md`), but Relay's receivers met the record
 * nested too: `payload.<entity>`, `payload.data.<entity>` (`candidate.hiringstage.updated`) and
 * `payload.data` (Relay `recruitment-portal/api/webhook-rcrm` `extractCandidate`, edge function
 * `routeEvent`). The first of those that names a record (an `id`, a `slug`, or a `resource_url` /
 * `url` that may carry the slug) wins, in that order, then the body itself; null when none does.
 */
export function rcrmWebhookRecord(payload, entity) {
    const key = RCRM_RECEIVER_SINGULAR[entity];
    const data = asRecord(payload['data']);
    const nested = asRecord(payload[key]) ?? asRecord(data?.[key]);
    for (const record of [nested, data, payload])
        if (namesARecord(record))
            return record;
    return null;
}
/** A delivered record's RCRM numeric id (as text) and slug: what the CRM finds it by. */
export function rcrmRecordRef(record, entity) {
    const id = record['id'];
    return {
        id: typeof id === 'number' || (typeof id === 'string' && id.trim() !== '')
            ? String(id).trim()
            : null,
        slug: rcrmRecordSlug(record, entity),
    };
}
/**
 * A record's RCRM slug: its `slug`, else the last path segment of its `resource_url` / `url` when
 * that segment follows the entity's word (`…/candidate/<slug>`; Relay `extractSlug`). Null when
 * neither says.
 */
export function rcrmRecordSlug(record, entity) {
    const own = record['slug'];
    if (typeof own === 'number')
        return String(own);
    if (typeof own === 'string' && own.trim() !== '')
        return own.trim();
    const pattern = new RegExp(`/${RCRM_RECEIVER_SINGULAR[entity]}s?/([^/?#]+)/?(?:[?#].*)?$`, 'i');
    for (const field of ['resource_url', 'url']) {
        const url = record[field];
        const segment = typeof url === 'string' ? pattern.exec(url)?.[1] : undefined;
        if (!segment)
            continue;
        try {
            return decodeURIComponent(segment);
            // eslint-disable-next-line crm/no-silent-catch -- a malformed escape is not a slug; the record may still be found by its id
        }
        catch {
            continue;
        }
    }
    return null;
}
/**
 * Who a change written by the receiver is by, in the audit log (and so in its event). API keys
 * are labelled `API key: <name>`, so this label cannot be taken by one.
 */
export const RCRM_RECEIVER_ACTOR = {
    type: 'integration',
    id: 'recruitcrm',
    label: 'RecruitCRM',
};
/**
 * Echo detection: an event the receiver caused carries the RCRM data already, so the CRM → RCRM
 * sync must not send it back (TS-1831 decision 5, `cameFromRcrm`). Takes the outbox row's `actor`
 * as stored (an `EventActor` in JSON).
 */
export function isRcrmReceiverActor(actor) {
    const a = actor;
    return a?.kind === 'integration' && a.name === RCRM_RECEIVER_ACTOR.label;
}
/**
 * Why one linked value in a webhook is not written (reported by the CRM field's KEY, never a
 * value): `type-pair-unsupported` (outside `RCRM_FIELD_TYPE_PAIRS`; only a hand-written row can
 * hold one), `format-pending` (only the PUSH of multiselect, user and phonenumber waits on a confirmed write; the
 * receiver reads multiselect and phonenumber), `user-map-missing` (an RCRM user field: no RCRM user ↔ CRM
 * member map exists yet, TS-1960),
 * `collides-with-column` (an RCRM id a CRM column owns), `rcrm-type-changed` /
 * `rcrm-field-renamed` (the entry's type or normalised name differs from the link's snapshot: an
 * id shift would write the wrong field), `crm-option-missing` (a dropdown label no CRM option
 * has), `invalid-value` (a value that does not parse as the CRM field's type), `stale-echo` (a
 * value read back from RCRM's API that equals what the CRM changed the field FROM a few minutes
 * ago: RCRM's reads lag its writes, so it is the pre-write value, not an RCRM change —
 * `docs/nitpicks/recruitcrm/stale-read-after-write.md`). For a candidate's Stage (key `stage`,
 * `planInboundStage`): `unknown-stage` (no stage of the default Candidate Flow pipeline has that
 * name) and `place-archived` (the candidate's place there is archived: restored first, as in the
 * API). For a deal's stage (key `stage`, the project's place in the pipeline linked `both` to RCRM
 * Deals): `pipeline-not-linked`, `pipeline-archived`, `not-in-pipeline` (the project has no place
 * there; the receiver does not add it), `place-archived`, `unknown-stage` (no stage of that
 * pipeline has the label's slug) and `stale-echo` (a stage the CRM moved the project FROM a few
 * minutes ago: the webhook of the CRM's own earlier push). For a CREATION (keys `company`,
 * `contact`, `deal`; TS-1953): `ambiguous-match` (the CRM already holds a record that may be this
 * one — same name or e-mail — that the sync's rule does not confirm: nothing is created, an
 * operator links it), `missing-data` (the record names nothing to create it by: no name, no
 * company) and `sales-pipeline-missing` (a deal needs RCRM - Sales and a default Deal Flow). For a deal
 * UPDATE's job slugs (key `jobs`): `job-on-other-rcrm-deal` (a CRM job the deal names sits on another
 * deal that is itself linked to RCRM: not moved).
 */
export const RCRM_RECEIVER_SKIP_REASONS = [
    'type-pair-unsupported',
    'format-pending',
    'user-map-missing',
    'collides-with-column',
    'rcrm-type-changed',
    'rcrm-field-renamed',
    'crm-option-missing',
    'invalid-value',
    'stale-echo',
    'unknown-stage',
    'place-archived',
    'pipeline-not-linked',
    'pipeline-archived',
    'not-in-pipeline',
    'ambiguous-match',
    'missing-data',
    'sales-pipeline-missing',
    'job-on-other-rcrm-deal',
];
/**
 * How one delivery ended once the worker applied it (stored on its inbox row). `updated`: at
 * least one linked value, or a candidate's Stage, changed. `unchanged`: none did (or none is linked). `unknown-record`: the
 * CRM has no live record linked to that RCRM id (nothing is created from RCRM).
 * `not-in-recruitcrm`: the delivery carried no `custom_fields` and RCRM no longer has the record
 * to read them from. `created` (TS-1953): a creation webhook for a record the CRM did not know made
 * it (a deal also its project, company and contacts), or adopted a CRM record that IS it. `not-created`:
 * it could not or should not — the skips say why.
 */
export const RCRM_RECEIVER_OUTCOMES = [
    'updated',
    'unchanged',
    'unknown-record',
    'not-in-recruitcrm',
    'created',
    'not-created',
];
export const rcrmReceiverResultSchema = z.object({
    outcome: z.enum(RCRM_RECEIVER_OUTCOMES),
    /** The CRM field keys whose value changed (`stage` for a candidate's Stage). */
    changed: z.array(z.string()),
    skipped: z.array(z.object({ key: z.string(), reason: z.enum(RCRM_RECEIVER_SKIP_REASONS) })),
});
/** A creation webhook makes the record when the CRM has none (TS-1953): every receiver entity. */
export function rcrmCreatesRecords(entity) {
    return RCRM_RECEIVER_ENTITIES.includes(entity);
}
/**
 * What the receiver answers RCRM. RCRM never re-delivers, so the API only STORES a delivery
 * (`rcrm_webhook_inbox`) and the worker applies it with retries: `queued` (stored), `disabled`
 * (`recruitcrm.receive` off: ignored, not stored), `unidentified` (no record found in the body — no
 * id, slug or URL — so nothing could ever be matched; logged with the body's keys).
 */
export const RCRM_WEBHOOK_ACKS = ['queued', 'disabled', 'unidentified'];
export const rcrmWebhookAckSchema = z.object({
    outcome: z.enum(RCRM_WEBHOOK_ACKS),
    /** The inbox row, when queued. */
    id: z.uuid().optional(),
});
/**
 * An inbox row's life: `pending` (new, or waiting on its retry), `done` (applied, with an
 * outcome), `dead` (gave up: retries exhausted, or nothing to read it by).
 */
export const RCRM_INBOX_STATUSES = ['pending', 'done', 'dead'];
/**
 * Which RCRM record a delivery is about, for per-record ORDER: a later delivery of a record waits
 * while an earlier one is still pending, so a retried old value never overwrites a newer one.
 * The numeric id when the delivery has it, else the slug.
 */
export function rcrmInboxRecordKey(entity, ref) {
    if (ref.id)
        return `${entity}:id:${ref.id}`;
    if (ref.slug)
        return `${entity}:slug:${ref.slug}`;
    return null;
}
//# sourceMappingURL=webhooks.js.map